import {
	CheckCircleFilled,
	CloseCircleFilled,
	FormOutlined,
	LoadingOutlined,
	PlusOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import { Button, Col, Row, Space, Spin } from 'antd';
import Text from 'antd/lib/typography/Text';
import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { CustomNotification } from 'src/components/custom-notification';
import { tableColumnTextFilterConfig } from 'src/components/tableUtils';

import {
	MutationDeleteIntroVideo,
	MutationUpdateIntroVideo,
} from 'src/lib/gql/mutations';
import { QueryGetIntroVideos } from 'src/lib/gql/queries';
import { IntroVideoTable } from 'src/lib/interface';
import InfoTable from '../components/info-table';
import { ROUTES } from '../config/route';
import {
	CmsDeleteIntroVideoMutation,
	CmsDeleteIntroVideoMutationVariables,
	CmsGetIntroVideosQueryVariables,
	CmsUpdateIntroVideoMutation,
	CmsUpdateIntroVideoMutationVariables,
} from 'src/lib/generated/_generated';

const IntroVideo = () => {
	const {
		data: qryData,
		loading,
		refetch: refetchQuery,
	} = useQuery<CmsGetIntroVideosQueryVariables>(QueryGetIntroVideos, {
		fetchPolicy: 'cache-and-network',
	});

	const [UpdateIntroVideo] = useMutation<
		CmsUpdateIntroVideoMutation,
		CmsUpdateIntroVideoMutationVariables
	>(MutationUpdateIntroVideo, {
		onError(error) {
			CustomNotification({
				pageName: 'Intro Video',
				pagePrefix: 'Video',
				notificationType: 'custom-error',
				customDescription: error.message,
			});
		},
	});

	const updateVideo = useCallback(
		async (id: string, enabled: boolean | undefined) => {
			const rsp = await UpdateIntroVideo({
				variables: {
					id: id,
					enabled: enabled,
				},
			});

			if (rsp.data) {
				CustomNotification({
					pageName: 'Intro Video',
					pagePrefix: 'Video',
					notificationType: 'updated',
				});

				refetchQuery();
			}
		},
		[qryData?.getIntroVideos],
	);

	const [DeleteIntroVideo] = useMutation<
		CmsDeleteIntroVideoMutation,
		CmsDeleteIntroVideoMutationVariables
	>(MutationDeleteIntroVideo, {
		onError(error) {
			CustomNotification({
				pageName: 'Intro Video',
				pagePrefix: 'Video',
				notificationType: 'custom-error',
				customDescription: error.message,
			});
		},
	});

	const deleteVideo = useCallback(
		async (id: string) => {
			if (!confirm('Confirm delete?')) return;
			const rsp = await DeleteIntroVideo({
				variables: {
					id: id,
				},
			});

			if (rsp.data) {
				CustomNotification({
					pageName: 'Intro Video',
					pagePrefix: 'Video',
					notificationType: 'deleted',
				});

				refetchQuery();
			}
		},
		[qryData?.getIntroVideos],
	);

	const p_IntroVideos: IntroVideoTable[] =
		(qryData?.getIntroVideos as any)?.map((video, i) => {
			return {
				id: video?.id || '',
				name: video?.name || '',
				link: video?.video?.url || '',
				enabled: video?.enabled || false,
				key: (i + 1).toString() || '',
			};
		}) || [];

	// Fields
	const columns = [
		{
			title: 'ID',
			dataIndex: 'key',
			width: 50,
			align: 'center',
			render: (key: number) => {
				return key;
			},
		},
		{
			title: 'Title',
			dataIndex: 'name',
			key: 'title',
			width: 300,
			...tableColumnTextFilterConfig<IntroVideoTable>(),
			onFilter: (value, record) => {
				return record.name
					.toString()
					.toLowerCase()
					.includes(value.toString().toLowerCase());
			},
		},
		{
			title: 'Link',
			dataIndex: 'link',
			key: 'link',
			width: 300,
			ellipsis: true,
		},

		{
			title: 'Edit',
			key: 'enabled',
			width: 100,
			align: 'center',
			filters: [
				{
					text: 'Enabled',
					value: true,
				},
				{
					text: 'Disabled',
					value: false,
				},
			],
			filterMultiple: false,
			onFilter: (value, record) => record.enabled === value,
			render: (props: { id: string; enabled: boolean }) => {
				const { id, enabled } = props;
				return (
					<Space>
						<Link to={`${ROUTES.EDIT_INTRO_VIDEO.path}/${id}`}>
							<FormOutlined
								style={{
									color: 'black',
								}}
							/>
						</Link>
						<CheckCircleFilled
							onClick={() => {
								updateVideo(id, !enabled);
							}}
							style={{
								color: enabled ? 'var(--valid)' : 'var(--inactive)',
							}}
						/>
						<CloseCircleFilled
							onClick={() => {
								deleteVideo(id);
							}}
							style={{
								color: 'var(--invalid)',
							}}
						/>
					</Space>
				);
			},
		},
	];

	return (
		<Row>
			<Col span={24}>
				<Text className="sub-header">Intro Video</Text>
			</Col>

			<Col span={24}>
				<Row
					gutter={16}
					style={{
						margin: '16px 0',
					}}
				>
					<Link to={ROUTES.CREATE_INTRO_VIDEO.path}>
						<Button className="btn-main">
							Create New Intro Video
							<PlusOutlined
								style={{
									position: 'relative',
									top: '1.5px',
								}}
							/>
						</Button>
					</Link>
				</Row>

				<Spin indicator={<LoadingOutlined />} spinning={loading ? true : false}>
					<InfoTable<IntroVideoTable>
						data={p_IntroVideos}
						columnData={columns}
						onChange={() => {
							//
						}}
						other={{
							scroll: {
								x: 900,
							},
						}}
					/>
				</Spin>
			</Col>
		</Row>
	);
};

export default IntroVideo;
